import { batch } from 'react-redux';
import { noop, set } from 'lodash';
import { dimensionSelector, selectedAccountSelector } from 'selectors';
import { reportsApi } from 'services/api';
import { ERROR_CODES } from 'services/api/apiConstants';
import { HTTP_HEADERS_PROPERTY } from 'services/api/apiConstants';
import { getRrandFormResponse } from 'services/callAjax/responseParserUtils';
import { COMPONENT_STATUS } from 'services/constants';
import { columnDefsStateSelector } from '../../../selectors';
import {
    getReportAbort,
    getReportError,
    getReportSuccess,
    requestGetReport,
    setColumnDef,
    setReportDataStatus,
    setReportDuration,
} from '../actions';
import { DEFAULT_REPORT_PAGE } from '../hooks';
import { filterReportSortByColumnDefs, flatDynamicFields } from '../services';
import { getColumnDefsWithDynamicColumns } from '../services/dynamicColumnsService';
import addLastUpdateTime from '../utils/lastUpdateTimeReportRowTransformer';
import { getDateRangeQueryParam, getReportFiltersParam, getReportQueryParams } from '../utils/queryFilterUtils';

const generateReportRowId = (row, reportId, rowFieldId) => `${reportId}_${row[rowFieldId]}`;

const transformReportData = ({
    results: reportData,
    lastUpdateTime,
    reportId,
    reportConfig: { rowIdField, lastUpdateTimeField },
    timeZoneName,
}) => {
    return reportData.map(({ dynamicFields, ...restRow }) => ({
        gridRowId: generateReportRowId(restRow, reportId, rowIdField),
        id: restRow[rowIdField],
        ...restRow,
        ...addLastUpdateTime({ rowData: restRow, lastUpdateTimeField, lastUpdateTime, timeZoneName }),
        ...flatDynamicFields(dynamicFields),
    }));
};

const defaultTransformTotal = ({ dynamicFields, ...restTotal } = {}) => ({
    ...restTotal,
    ...flatDynamicFields(dynamicFields),
});

const transformReport = ({
    data: { results = [], total, lastUpdateTime, metadata, ...rest },
    reportId,
    reportConfig,
    timeZoneName,
}) => {
    const transformTotal = reportConfig?.transformTotalOverride || defaultTransformTotal;
    return {
        results: transformReportData({
            results,
            lastUpdateTime,
            reportId,
            reportConfig,
            timeZoneName,
        }),
        total: total ? transformTotal(total) : null,
        metadata: { reportId, ...metadata },
        ...rest,
    };
};

const hookResponse = () => {
    const responseHeaders = {};
    const onResponse = ({ response, networkFailureCount } = {}) => {
        set(responseHeaders, HTTP_HEADERS_PROPERTY.RRAND, getRrandFormResponse(response));
        if (networkFailureCount) {
            set(responseHeaders, 'networkFailureCount', networkFailureCount);
        }
    };

    return { responseHeaders, onResponse };
};

const getReport =
    ({
        accountId,
        reportId,
        startDate,
        endDate,
        reportEntityIds,
        reportEntityType,
        reportPageSize,
        reportPage = DEFAULT_REPORT_PAGE,
        reportSort,
        searchTerm,
        reportFilters,
        reportQueryParams,
        reportConfig,
        allowedSortingColumnDefs = [],
        saveColumnStateByPresetAndColumnDefs,
        reportPresetName,
        reportPresetNameGetter,
        requestHashGetter,
        getReportTraceRequestHeader = () => ({}),
        setReportTraceAttributes = noop,
    }) =>
    (dispatch, getState) => {
        const state = getState();
        const { timeZoneName } = selectedAccountSelector(state);
        const columnDefs = columnDefsStateSelector(state);
        const dimension = dimensionSelector(state);

        batch(() => {
            if (!columnDefs.length) {
                dispatch(setColumnDef(reportConfig.columnsDef));
            }
        });

        let resolvedReportSort = filterReportSortByColumnDefs(reportSort, columnDefs, allowedSortingColumnDefs);
        const filterQueryParams = getReportFiltersParam({
            reportEntityIds,
            reportEntityType,
            searchTerm,
            campaignIdFilterName: reportConfig.campaignIdFilterName,
            searchField: reportConfig.searchField,
            dynamicFilters: reportFilters,
        });

        const queryParams = {
            ...getDateRangeQueryParam(startDate, endDate),
            ...filterQueryParams,
            ...getReportQueryParams(reportPage, reportPageSize, {
                reportSort: resolvedReportSort,
                sortMap: reportConfig.sortMap,
            }),
            dimension,
            ...reportQueryParams,
            postBody: reportConfig?.postBody,
        };
        const reportStart = Date.now();

        dispatch(requestGetReport());
        const requestHash = requestHashGetter();

        const { responseHeaders, onResponse } = hookResponse();

        return reportsApi(reportConfig.endpoint, accountId, queryParams, getReportTraceRequestHeader, onResponse)
            .then(data =>
                batch(() => {
                    if (requestHash !== requestHashGetter()) {
                        return {};
                    }
                    const reportDuration = (Date.now() - reportStart) / 1000;
                    setReportTraceAttributes({
                        gridFetchTime: reportDuration,
                        rrand: responseHeaders[HTTP_HEADERS_PROPERTY.RRAND],
                        networkFailureCount: responseHeaders.networkFailureCount,
                    });
                    const report = transformReport({ data, reportId, reportConfig, timeZoneName });

                    dispatch(getReportSuccess({ reportId, ...report, reportConfig, reportDataRaw: data }));
                    dispatch(setReportDuration(reportDuration, reportPresetName));
                    dispatch(setReportDataStatus(COMPONENT_STATUS.ACTIVE));
                    const columnDefs = getColumnDefsWithDynamicColumns(reportId, reportConfig, report.metadata);

                    dispatch(setColumnDef(columnDefs));
                    resolvedReportSort = filterReportSortByColumnDefs(reportSort, columnDefs, allowedSortingColumnDefs);

                    saveColumnStateByPresetAndColumnDefs({
                        reportConfig,
                        reportId,
                        reportPresetName: reportPresetNameGetter(),
                        reportSorting: resolvedReportSort,
                        columnDefs,
                        autoUpdate: true,
                    });

                    return { data };
                })
            )
            .catch(error =>
                batch(() => {
                    if (error.status === ERROR_CODES.ABORT) {
                        dispatch(getReportAbort());
                    } else {
                        setReportTraceAttributes({
                            rrand: responseHeaders[HTTP_HEADERS_PROPERTY.RRAND],
                            networkFailureCount: responseHeaders.networkFailureCount,
                        });
                        dispatch(getReportError(error));
                        dispatch(setReportDataStatus(COMPONENT_STATUS.ERROR));
                    }

                    return { error };
                })
            );
    };

export default getReport;
